import axios from 'axios';
import { ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import React, { useState, useEffect } from 'react';
import { NODE_BACKEND_API_URL } from '../../config';
import afipQueueService from '../../services/afipQueueService';

function UptimeRegistry() {
    // Demo mode: uptime is a real-server metric — not shown in the demo
    if (process.env.REACT_APP_DEMO_MODE === 'true') return null;

    // State to hold the uptime data object
    const [uptimeData, setUptimeData] = useState(null);
    // State for loading and error handling
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // State for AFIP health status
    const [afipHealth, setAfipHealth] = useState(null);
    const [afipLoading, setAfipLoading] = useState(true);

    useEffect(() => {
        // Fetch server uptime
        axios.get(`${NODE_BACKEND_API_URL}/getuptime`)
            .then(res => {
                // Assuming the API now returns an object like { days, hours, minutes, seconds }
                setUptimeData(res.data);
                setLoading(false);
            })
            .catch(er => {
                console.error("Error fetching uptime data:", er);
                setError("Error al cargar el tiempo de actividad.");
                setLoading(false);
            });
        
        // Fetch AFIP health status
        const fetchAfipHealth = async () => {
            try {
                const result = await afipQueueService.checkAFIPHealth();
                if (result.success && result.data) {
                    setAfipHealth(result.data);
                } else {
                    setAfipHealth({ isHealthy: false });
                }
            } catch (error) {
                console.error("Error fetching AFIP health:", error);
                setAfipHealth({ isHealthy: false });
            } finally {
                setAfipLoading(false);
            }
        };
        
        fetchAfipHealth();
        
        // Poll AFIP health every 60 seconds
        const interval = setInterval(fetchAfipHealth, 60000);
        
        return () => clearInterval(interval);
    }, []); // Empty dependency array means this effect runs once on mount

    // Helper function to format numbers with leading zeros
    const formatTime = (num) => num.toString().padStart(2, '0');

    // Calculate formatted uptime string and gradient style
    let formattedUptime = 'Cargando...';
    let gradientStyle = {}; // Default or loading style

    if (!loading && !error && uptimeData) {
        // Format uptime as DD:HH:MM
        const { days, hours, minutes } = uptimeData;
        formattedUptime = `PH-SERVER: ${formatTime(days)}dd:${formatTime(hours)}hh:${formatTime(minutes)}mm`;

        // Calculate total hours for gradient logic (including fractional hours)
        const totalHours = uptimeData.totalSeconds / 3600;

        // Determine gradient style based on total hours
        if (totalHours < 1) {
            // Less than 1 hour: Transition from Red towards Yellow
            // As it approaches 1 hour, yellow becomes more prominent
            const yellowStop = Math.min(totalHours, 1) * 100; // Position of pure yellow stop (0% to 100%)
            gradientStyle.background = `linear-gradient(to right, var(--color-gradient-red), var(--color-gradient-yellow) ${yellowStop}%)`;
        } else if (totalHours < 12) {
            // Between 1 and 12 hours: Transition from Yellow towards Green
            // As it approaches 12 hours, green becomes more prominent
            const percentageInRange = (totalHours - 1) / (12 - 1); // Normalize to 0 to 1
            const greenStop = percentageInRange * 100; // Position of pure green stop (0% to 100% within range)
            gradientStyle.background = `linear-gradient(to right, var(--color-gradient-yellow), var(--color-gradient-green) ${greenStop}%)`;
        } else {
            // More than 12 hours: Mostly Green
             gradientStyle.background = `linear-gradient(to right, var(--color-gradient-yellow) 0%, var(--color-gradient-green) 100%)`;
             // Or simpler: gradientStyle.backgroundColor = 'var(--color-gradient-green)';
        }
    }

    // Render loading, error, or data
    if (loading) {
        return <div className='card'><div className='div-uptime' style={gradientStyle}>Cargando tiempo de actividad...</div></div>;
    }

    if (error) {
         return <div className='card'><div className='div-uptime' style={gradientStyle}>Error al cargar tiempo de actividad: {error}</div></div>;
    }

    // If data is loaded successfully
    return (
        <div className='card'>
            <div className='card-inner'>
                <ThunderboltOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <h2>UPTIME</h2>
            </div>
            <br />
            
            {/* Two-column layout for status badges */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '12px',
                width: '100%'
            }}>
                {/* Server Uptime Status - Left Column */}
                <div style={{ 
                    padding: '16px 12px', 
                    borderRadius: '8px',
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '70px',
                    textAlign: 'center'
                }}>
                    <div style={{ 
                        fontSize: '24px', 
                        fontWeight: '500',
                        color: '#52c41a',
                        marginBottom: '4px'
                    }}>
                        <CheckCircleOutlined style={{ marginRight: '6px' }} />
                        Servidor: Online
                    </div>
                    <div style={{ 
                        fontSize: '18px', 
                        color: '#52c41a',
                        fontWeight: '400'
                    }}>
                        ({uptimeData?.days || 0} días)
                    </div>
                </div>
                
                {/* AFIP Health Status - Right Column */}
                <div style={{ 
                    padding: '16px 12px', 
                    borderRadius: '8px',
                    background: afipLoading ? '#f0f0f0' : (afipHealth?.isHealthy ? '#f6ffed' : '#fff1f0'),
                    border: afipLoading ? '1px solid #d9d9d9' : (afipHealth?.isHealthy ? '1px solid #b7eb8f' : '1px solid #ffa39e'),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '70px',
                    textAlign: 'center'
                }}>
                    <div style={{ 
                        fontSize: '24px', 
                        fontWeight: '500',
                        color: afipLoading ? '#8c8c8c' : (afipHealth?.isHealthy ? '#52c41a' : '#ff4d4f'),
                        marginBottom: '4px'
                    }}>
                        {afipLoading ? (
                            'Verificando AFIP...'
                        ) : afipHealth?.isHealthy ? (
                            <>
                                <CheckCircleOutlined style={{ marginRight: '6px' }} />
                                AFIP: Online
                            </>
                        ) : (
                            <>
                                <CloseCircleOutlined style={{ marginRight: '6px' }} />
                                AFIP: Offline
                            </>
                        )}
                    </div>
                    {!afipLoading && afipHealth?.timestamp && (
                        <div style={{ 
                            fontSize: '18px', 
                            color: afipHealth?.isHealthy ? '#52c41a' : '#ff4d4f',
                            fontWeight: '400'
                        }}>
                            (Chequeado {new Date(afipHealth.timestamp).toLocaleTimeString('es-AR', { hour12: false })})
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UptimeRegistry;